import { IFeatureService } from '@project/services';
import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { SocketMessagesDataProviderService } from '@project/data-providers';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class PatientQueuePositionService implements IFeatureService {
  private readonly _patientQueuePosition$ = new BehaviorSubject<number>(null);
  public readonly patientQueuePosition$ = this._patientQueuePosition$.asObservable();

  private destroy$ = new Subject();

  constructor(private socketMessagesProviderService: SocketMessagesDataProviderService) {}

  initialise(): Observable<void> {
    // When we receive a queue update, we check if we need to update the position
    this.subscribeToQueueUpdate((newPosition) => this.handleQueueUpdate(newPosition));

    // When an appointment is created or archived, we reset the queue position
    this.subscribeToAppointmentEvents(() => this.updateCounter(null));

    return of(null);
  }

  destroy(): Observable<void> {
    this.destroy$.next();
    return of(null);
  }

  private handleQueueUpdate(newPosition: number): void {
    const currentPosition = this._patientQueuePosition$.value;
    const shouldUpdateQueuePosition = this.shouldUpdateQueuePosition(newPosition, currentPosition);

    if (!shouldUpdateQueuePosition) return;

    this.updateCounter(newPosition);
  }

  private shouldUpdateQueuePosition(newPosition: number, currentPosition: number | null): boolean {
    const hasNoPositionYet = currentPosition === null;
    const hasPositionDecreased = currentPosition !== null && newPosition < currentPosition;

    return hasNoPositionYet || hasPositionDecreased;
  }

  private subscribeToQueueUpdate(cb: (queuePosition: number) => void) {
    this.socketMessagesProviderService.queueInfoUpdate$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
      cb(info.length);
    });
  }

  private subscribeToAppointmentEvents(cb: () => void) {
    merge(this.socketMessagesProviderService.chatCreated$, this.socketMessagesProviderService.chatArchived$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        cb();
      });
  }

  private updateCounter(value: number | null): void {
    this._patientQueuePosition$.next(value);
  }
}
